import React, { useState } from 'react'
import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import DialogContent from '@mui/material/DialogContent'
import DialogActions from '@mui/material/DialogActions'
import Typography from '@mui/material/Typography'
import IconButton from '@mui/material/IconButton'
import Button from '@mui/material/Button'
import Box from '@mui/material/Box'
import Grid from '@mui/material/Grid'
import Paper from '@mui/material/Paper'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableRow from '@mui/material/TableRow'
import CloseIcon from '@material-ui/icons/Close'
import moment from 'moment-timezone'
import { getLeadDisplayName } from 'config/helpers/Helpers'

const CONTACT_FIELDS = ['firstName', 'lastName', 'email', 'phone', 'dob', 'gender', 'smokingStatus', 'maritalStatus', 'address', 'city', 'county', 'eircode', 'country', 'employmentStatus', 'employerSickPay', 'occupation', 'salary', 'majorHealthIssue', 'privacyPolicy']
const TRACKING_FIELDS = ['source', 'originalSource', 'adSource', 'utmParams', 'gclid', 'fbclid', 'fbp', 'uuid', 'referrer', 'lastInternalPage', 'ip']

function formatValue(value) {
	if (value === null || value === undefined || value === '') return '-'
	if (typeof value === 'boolean') return value ? 'Yes' : 'No'
	if (typeof value === 'object') return JSON.stringify(value)
	return String(value)
}

function FieldTable({ rows }) {
	return (
		<Table size='small'>
			<TableBody>
				{rows.map(([label, value]) => (
					<TableRow key={label}>
						<TableCell sx={{ width: '40%', color: 'text.secondary', verticalAlign: 'top' }}>{label}</TableCell>
						<TableCell sx={{ wordBreak: 'break-word' }}>{formatValue(value)}</TableCell>
					</TableRow>
				))}
			</TableBody>
		</Table>
	)
}

function Section({ title, children }) {
	return (
		<Paper variant='outlined' sx={{ p: 2, mb: 2 }}>
			<Typography variant='subtitle2' gutterBottom>{title}</Typography>
			{children}
		</Paper>
	)
}

export default function SandboxLeadModal(props) {
	const lead = props.lead || {}
	const [showRaw, setShowRaw] = useState(false)

	const pick = (fields) => fields.filter((field) => lead[field] !== undefined).map((field) => [field, lead[field]])
	const quoteRows = Object.entries(lead.quote || {})
	const notes = Array.isArray(lead.notes) ? lead.notes : []
	const pageVisits = Array.isArray(lead.pageVisits) ? lead.pageVisits : []

	return (
		<Dialog open={props.isOpen} onClose={() => props.close()} maxWidth='lg' fullWidth>
			<DialogTitle sx={{ pr: 6 }}>
				{getLeadDisplayName(lead.firstName, lead.lastName)}
				<Typography variant='body2' color='text.secondary'>{lead.phone} · {lead.email}</Typography>
				<IconButton onClick={() => props.close()} sx={{ position: 'absolute', right: 8, top: 8 }}>
					<CloseIcon />
				</IconButton>
			</DialogTitle>

			<DialogContent dividers>
				<Grid container spacing={2}>
					<Grid item xs={12} md={6}>
						<Section title='Submission'>
							<FieldTable rows={[
								['product', lead.product],
								['submitted', lead.createdAt ? moment(lead.createdAt).format('DD/MM/YYYY HH:mm:ss') : null],
								['isValid', lead.isValid],
								['invalidReason', lead.invalidReason],
								['date', lead.date],
								['leadId', lead.leadId],
								...pick(['isDuplicate', 'duplicateOf', 'duplicateCompanyId'])
							]} />
						</Section>
						<Section title='Contact'>
							<FieldTable rows={pick(CONTACT_FIELDS)} />
						</Section>
					</Grid>
					<Grid item xs={12} md={6}>
						<Section title='Quote'>
							{quoteRows.length > 0 ? <FieldTable rows={quoteRows} /> : <Typography variant='body2' color='text.secondary'>No quote fields</Typography>}
						</Section>
						<Section title='Tracking'>
							<FieldTable rows={pick(TRACKING_FIELDS)} />
						</Section>
						<Section title={`Page visits (${pageVisits.length})`}>
							{pageVisits.length > 0
								? <FieldTable rows={pageVisits.map((visit, index) => [
									visit.timestamp ? moment(visit.timestamp).format('DD/MM HH:mm:ss') : `#${index + 1}`,
									visit.page || visit.url || visit
								])} />
								: <Typography variant='body2' color='text.secondary'>None sent</Typography>
							}
						</Section>
						{notes.length > 0 &&
							<Section title='Notes'>
								<FieldTable rows={notes.map((note, index) => [note.category || `note ${index + 1}`, note.note])} />
							</Section>
						}
					</Grid>
					<Grid item xs={12}>
						<Button size='small' variant='outlined' onClick={() => setShowRaw(!showRaw)}>
							{showRaw ? 'Hide raw JSON' : 'Show raw JSON'}
						</Button>
						{showRaw &&
							<Box component='pre' sx={{ mt: 2, p: 2, bgcolor: 'grey.100', fontSize: 12, overflowX: 'auto' }}>
								{JSON.stringify(lead, null, 2)}
							</Box>
						}
					</Grid>
				</Grid>
			</DialogContent>

			<DialogActions>
				<Button onClick={() => props.close()}>Close</Button>
			</DialogActions>
		</Dialog>
	)
}
